<template>
  <div class="space-y-2">
    <!-- Overview: event phase window + incident markers, aligned to the same
         time window the per-resource lanes use below. -->
    <div class="flex items-center gap-2">
      <div class="w-28 shrink-0 text-xs uppercase tracking-wide text-white/45 sm:w-40">Event</div>
      <div ref="binProbe" class="flex-1">
        <bly-event-analytics-timeline-row
          :event="event"
          :incidents="groups"
          :selected-incident-id="selectedIncidentId"
          :bin-count="binCount"
          :has-report="true"
          hide-header
          hide-axis
          @select-incident="$emit('select-incident', { group: $event, anomalyId: null })"
        />
      </div>
    </div>

    <!-- Per input/output resource lanes -->
    <div
      v-for="section in sections"
      :key="`sec-${section.key}`"
      class="space-y-1"
    >
      <div
        class="flex items-center gap-1 pt-1 text-xs font-semibold uppercase tracking-wide"
        :class="section.colorClass"
      >
        <i :class="section.icon"></i>{{ section.label }}
        <span class="text-white/35">({{ section.resources.length }})</span>
      </div>

      <div
        v-for="resource in section.resources"
        :key="resource.key"
        class="flex items-center gap-2"
      >
        <div
          class="flex w-28 shrink-0 items-center gap-1 text-xs sm:w-40"
          :class="section.colorClass"
          :title="resource.name"
        >
          <span class="truncate">{{ resource.name }}</span>
          <span v-if="resource.bars.length" class="ml-auto shrink-0 text-[10px] text-white/40">{{ resource.bars.length }}</span>
        </div>
        <div class="relative h-5 flex-1 overflow-hidden rounded-sm border border-white/10 bg-black/40">
          <button
            v-for="(bar, barIndex) in resource.bars"
            :key="`${bar.id}-${barIndex}`"
            class="absolute inset-y-0 rounded-sm border-0 p-0 transition hover:opacity-90"
            :class="[bar.colorClass, bar.zClass, barRingClass(bar)]"
            :style="{ left: `${bar.left}%`, width: `${bar.width}%` }"
            :title="bar.title"
            @click="$emit('select-incident', { group: bar.group, anomalyId: bar.id })"
          ></button>
        </div>
      </div>
    </div>

    <!-- Shared time axis -->
    <div class="flex items-center gap-2">
      <div class="w-28 shrink-0 sm:w-40"></div>
      <div class="relative h-5 flex-1">
        <div
          v-for="mark in timeMarks"
          :key="mark.key"
          class="absolute top-0 flex flex-col whitespace-nowrap"
          :class="axisMarkClass(mark.align)"
          :style="{ left: `${mark.left}%` }"
        >
          <span class="h-1.5 w-px bg-white/35"></span>
          <span class="mt-0.5 text-[10px] text-white/55">{{ mark.label }}</span>
        </div>
      </div>
    </div>

    <div v-if="!sections.length" class="text-xs text-white/45">
      No input/output anomalies recorded for this event.
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import moment from 'moment';
// `Bly` prefix keeps the kebab-case template tag resolving under script-setup
// automatic component registration.
import BlyEventAnalyticsTimelineRow from './EventAnalyticsTimelineRow.vue';
import { resourceTypeMeta } from '../utils/eventResourceType';
import {
  createTimelineScale,
  severityBg,
  severityZClass,
  severityLabel,
  alignForLeft,
  axisMarkClass,
  dedupeMarks,
  validMs,
  firstMs,
} from '../utils/timelineScale';

// Render order for resource-type sections; anything else falls after these.
const TYPE_ORDER = ['INPUT', 'OUTPUT'];

// Status-track chunk sizing for the overview row — mirror the event list so the
// bins stay ~6px wide and adapt to the available width (see EventAnalytics.vue).
const CHUNK_TARGET_PX = 6;
const CHUNK_MIN = 40;
const CHUNK_MAX = 240;

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  // Incident groups (each carries its anomalies with resource info).
  groups: {
    type: Array,
    default: () => [],
  },
  selectedIncidentId: {
    type: String,
    default: null,
  },
  // Specific anomaly that is currently open in the drilldown (gets a solid
  // highlight); other bars in the same group get a fainter ring.
  selectedAnomalyId: {
    type: String,
    default: null,
  },
});

defineEmits(['select-incident']);

// Adapts to the overview-row width so bins match the event list (set in
// measureBins via a ResizeObserver).
const binCount = ref(CHUNK_MIN);
const binProbe = ref(null);
let binObserver = null;

// Derive the overview-row chunk count from its rendered width, clamped to
// the same bounds the event list uses.
function measureBins() {
  const el = binProbe.value;
  if (!el) return;
  const raw = Math.round(el.clientWidth / CHUNK_TARGET_PX);
  binCount.value = Math.max(CHUNK_MIN, Math.min(CHUNK_MAX, raw || CHUNK_MIN));
}

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined' && binProbe.value) {
    binObserver = new ResizeObserver(() => measureBins());
    binObserver.observe(binProbe.value);
  }
  measureBins();
});
onBeforeUnmount(() => {
  if (binObserver) {
    binObserver.disconnect();
    binObserver = null;
  }
});

function formatTime(value) {
  if (!value) return '--:--';
  return moment.utc(value).format('DD/MM HH:mm');
}
function formatTimeDetailed(value) {
  if (!value) return '--';
  return moment.utc(value).format('DD/MM HH:mm:ss');
}
function anomalyTooltip(anomaly) {
  const sev = severityLabel(anomaly.severity).toUpperCase();
  const name = anomaly.resourceName || anomaly.resourceId || '';
  const metric = anomaly.metricName ? ` | ${anomaly.metricName}` : '';
  const group = anomaly.groupId ? ` | ${anomaly.groupId}` : '';
  return `${sev} | ${name}${metric}${group}\n${formatTimeDetailed(anomaly.startMs)} -> ${formatTimeDetailed(anomaly.endMs)}`;
}
// Solid ring on the open anomaly, faint ring on its group-mates. The selected
// bar sits above the severity stacking (z-10..z-30) so it stays visible.
function barRingClass(bar) {
  if (props.selectedAnomalyId && bar.id === props.selectedAnomalyId) return 'ring-2 ring-inset ring-white z-40';
  if (bar.groupId === props.selectedIncidentId) {
    return props.selectedAnomalyId ? 'ring-1 ring-inset ring-white/40' : 'ring-2 ring-inset ring-white z-40';
  }
  return '';
}

// Whole timeline window: service padding + the event itself.
const lineupMs = computed(() => firstMs([props.event.serviceStartTime, props.event.lineupTime, props.event.startTime], 0, Math.min));
const endMs = computed(() => firstMs([props.event.serviceEndTime, props.event.endTime, props.event.startTime], lineupMs.value + 1, Math.max));
const eventStartMs = computed(() => {
  const value = validMs(props.event.startTime);
  if (value !== null) return value;
  const service = validMs(props.event.serviceStartTime);
  return service !== null ? service : lineupMs.value;
});
// Shared time -> percent scale for the whole event window.
const scale = computed(() => createTimelineScale({ start: lineupMs.value, end: endMs.value, minWidth: 0.8 }));
// Flatten anomalies, keeping a reference to the parent incident group so a
// click selects the group that drives the drilldown below.
const flatAnomalies = computed(() => {
  const list = [];
  (props.groups || []).forEach((group) => {
    (group.anomalies || []).forEach((anomaly) => {
      const startMs = Number(anomaly.startMs);
      const endMs2 = Number(anomaly.endMs);
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs2)) return;
      list.push({ anomaly, group, startMs, endMs: Math.max(endMs2, startMs) });
    });
  });
  return list;
});
// Base input/output resources declared on the event itself, so the lanes are
// always shown (with names) even when no report/anomalies exist.
const baseResources = computed(() => {
  const list = [];
  const addInput = (input) => {
    if (input) list.push({ type: 'INPUT', id: input.id, name: input.name || input.id || '—' });
  };
  addInput(props.event.input);
  addInput(props.event.inputOverride);
  (props.event.outputs?.items || []).filter(Boolean).forEach((item) => {
    const output = item.output || item;
    list.push({ type: 'OUTPUT', id: output.id, name: output.displayName || output.name || output.id || '—' });
  });
  return list;
});

// Group anomalies by resource (type + name) → ordered Input/Output sections,
// seeded with the event's own resources so empty lanes still render.
const sections = computed(() => {
  const byKey = new Map();
  const keyFor = (type, name) => `${type}::${name}`;

  baseResources.value.forEach((res) => {
    const key = keyFor(res.type, res.name);
    if (!byKey.has(key)) byKey.set(key, { key, type: res.type, name: res.name, id: res.id, bars: [] });
  });

  flatAnomalies.value.forEach((entry) => {
    const type = (entry.anomaly.resourceType || '').toString().toUpperCase() || 'OTHER';
    const name = entry.anomaly.resourceName || entry.anomaly.resourceId || '—';
    // Match a base lane by id or name, else fall back to a new type::name lane.
    let key = keyFor(type, name);
    if (!byKey.has(key)) {
      const id = entry.anomaly.resourceId;
      const match = id && Array.from(byKey.values()).find((r) => r.id && r.id === id);
      if (match) key = match.key;
      else byKey.set(key, { key, type, name, bars: [] });
    }
    const { left, width } = scale.value.bar(entry.startMs, entry.endMs);
    byKey.get(key).bars.push({
      id: entry.anomaly.id,
      groupId: entry.group.id,
      group: entry.group,
      left,
      width,
      colorClass: severityBg(entry.anomaly.severity),
      zClass: severityZClass(entry.anomaly.severity),
      title: anomalyTooltip(entry.anomaly),
    });
  });

  const resources = Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  const types = Array.from(new Set(resources.map((r) => r.type)))
    .sort((a, b) => {
      const ia = TYPE_ORDER.indexOf(a);
      const ib = TYPE_ORDER.indexOf(b);
      return (ia === -1 ? TYPE_ORDER.length : ia) - (ib === -1 ? TYPE_ORDER.length : ib);
    });

  return types.map((type) => {
    const meta = resourceTypeMeta(type);
    return {
      key: type,
      label: `${meta.label}s`,
      colorClass: meta.colorClass,
      icon: meta.icon,
      resources: resources.filter((r) => r.type === type),
    };
  });
});
const timeMarks = computed(() => {
  const marks = [
    { key: 'lineup', time: lineupMs.value, label: formatTime(props.event.lineupTime || lineupMs.value) },
    { key: 'start', time: eventStartMs.value, label: formatTime(props.event.startTime || eventStartMs.value) },
    { key: 'end', time: endMs.value, label: formatTime(props.event.endTime || endMs.value) },
  ];
  return dedupeMarks(
    marks.map((mark) => {
      const left = scale.value.percent(mark.time);
      return { ...mark, left, align: alignForLeft(left) };
    }),
  );
});
</script>
